import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { css, keyframes, styled } from "goober";

import { readState } from "@/__main__/app-state.mjs";
import ChevronLeft from "@/inline-assets/chevron-left-button.svg";
import CloseIcon from "@/inline-assets/close.svg";
import { classNames } from "@/util/class-names.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

export const dialogMobile = `@container dialog-container (width >= 100vw)`;

type DialogSizeProps =
  | { width: string | number }
  | { minWidth: string | number; maxWidth: string | number };

type DialogWindowProps = React.HTMLAttributes<HTMLDivElement> & {
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;

  animated?: boolean;

  children?: React.ReactNode;
} & DialogSizeProps;

/**
 * A dialog window that can be used to display content on top of the current page.
 *
 * Unless you need some very specific styling, these should follow a general structure
 * to ensure consistency:
 *
 * @example
 * ```tsx
 * <DialogWindow width={400}>
 *   <DialogContent>
 *     <DialogHeader title="Title" />
 *
 *     <DialogBody>
 *       <p>Content</p>
 *     </DialogBody>
 *
 *     <DialogFooter>
 *       <button>Button</button>
 *     </DialogFooter>
 *   </DialogContent>
 * </DialogWindow>
 * ```
 *
 * @example
 * ```tsx
 * <DialogWindow width={400}>
 *   <DialogContent>
 *     <DialogHeader title="Title" />
 *
 *     <form>
 *       <DialogBody>
 *         <p>Content</p>
 *       </DialogBody>
 *
 *       <DialogFooter>
 *         <button>Button</button>
 *       </DialogFooter>
 *     </form>
 *   </DialogContent>
 * </DialogWindow>
 * ```
 *
 * @example
 * ```tsx
 * <DialogWindow width={400}>
 *   <ModalNavigation.Navigator>
 *     <ModalNavigation.Route name="route1">
 *       <DialogContent>
 *         <DialogHeader title="Title" />
 *
 *         <DialogBody>
 *           <p>Content</p>
 *         </DialogBody>
 *
 *         <DialogFooter>
 *           <button>Button</button>
 *         </DialogFooter>
 *       </DialogContent>
 *     </ModalNavigation.Route>
 *     <ModalNavigation.Route name="route2">
 *       <DialogContent>
 *         <DialogHeader title="Title" />
 *
 *         <DialogBody>
 *           <p>Content</p>
 *         </DialogBody>
 *
 *         <DialogFooter>
 *           <button>Button</button>
 *         </DialogFooter>
 *       </DialogContent>
 *     </ModalNavigation.Route>
 *   </ModalNavigation.Navigator>
 * </DialogWindow>
 * ```
 */
export function DialogWindow({
  width,
  minWidth,
  maxWidth,
  animated = true,
  className,
  children,
  ...rest
}: DialogWindowProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const animationRef = useRef<Animation | null>(null);
  const prevHeight = useRef<number>(0);
  const hasResizedOnce = useRef<boolean>(false);

  const {
    settings: { disableAnimations },
  } = useSnapshot(readState);

  useEffect(() => {
    if (!animated || disableAnimations) return;

    const [durationStr, ...bezierArr] = getComputedStyle(contentRef.current)
      .getPropertyValue("--transition")
      .split(" ") ?? ["", ""];

    const duration =
      (durationStr.endsWith("ms") ? 1 : durationStr.endsWith("s") ? 1000 : 0) *
      parseFloat(durationStr);

    const bezier = bezierArr.join(" ");

    function startResizeAnimation() {
      if (!animated || !containerRef.current || !contentRef.current) {
        return;
      }

      // The content height including borders and other styles is often slightly different from its
      // inner content height. Offset the target height to account for this.
      const contentHeightDiff =
        contentRef.current.offsetHeight - contentRef.current.clientHeight;

      const targetHeight = contentRef.current.scrollHeight + contentHeightDiff;

      if (!hasResizedOnce.current) {
        prevHeight.current = targetHeight;

        // ResizeObservers may be called multiple times on the "first render" while React
        // is still doing its thing, so we naively wait for the next tick to start animating
        setTimeout(() => {
          hasResizedOnce.current = true;
        }, 0);

        return;
      }

      // If the container and the content are the same height, then the window itself will
      // resize with the container, so animating it will only cause visual glitches
      if (containerRef.current.offsetHeight === contentRef.current.offsetHeight)
        return;

      if (prevHeight.current === targetHeight) return;

      animationRef.current?.cancel();

      contentRef.current.classList.toggle(
        "hide-scrollbar",
        targetHeight <= contentRef.current.offsetHeight,
      );

      animationRef.current = contentRef.current.animate(
        [
          {
            // If we don't set the minimum height to the heightDiff, we get flickering since
            // the resize observer will start being fired due to the outer height changing
            height: `${Math.max(contentHeightDiff, prevHeight.current)}px`,
          },
          { height: `${targetHeight}px` },
        ],
        {
          duration,
          easing: bezier,
          iterations: 1,
        },
      );

      animationRef.current.onfinish = () => {
        hasResizedOnce.current = true;

        prevHeight.current = targetHeight;

        contentRef.current?.classList.remove("hide-scrollbar");
      };
    }

    const resizeObserver = new ResizeObserver(startResizeAnimation);

    resizeObserver.observe(bodyRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [animated, disableAnimations]);

  return (
    <DialogContainer
      ref={containerRef}
      data-portal-container
      {...classNames(
        css(
          width !== undefined
            ? typeof width === "number"
              ? {
                  "--min-width": `${width}px`,
                  "--max-width": `${width}px`,
                }
              : {
                  "--min-width": width,
                  "--max-width": width,
                }
            : {
                "--min-width":
                  typeof minWidth === "number" ? `${minWidth}px` : minWidth,
                "--max-width":
                  typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
              },
        ),
        className,
      )}
      {...rest}
    >
      <DialogBox ref={contentRef} className="dialog-box">
        <div ref={bodyRef}>{children}</div>
      </DialogBox>
    </DialogContainer>
  );
}

export function DialogContent({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <Content {...classNames("dialog-content", className)} {...rest}>
      {children}
    </Content>
  );
}

type DialogHeaderProps = Omit<React.HTMLAttributes<HTMLDivElement>, "title"> & {
  showClose?: boolean;
  onPressBack?: () => void;
  onPressClose?: () => void;

  Icon?: React.ReactNode;

  Leading?: React.ReactNode;
  Following?: React.ReactNode;

  children?: React.ReactNode;
} & (
    | { title: Translation | string; titleClass?: string; Title?: never }
    | { title?: never; titleClass?: never; Title: React.ReactNode }
  );

export function DialogHeader({
  Icon,
  title,
  titleClass,
  Title,
  Leading,
  Following,
  showClose = true,
  onPressBack,
  onPressClose,
  className,
  children,
  ...rest
}: DialogHeaderProps) {
  const { t } = useTranslation();

  return (
    <Header {...classNames("dialog-header", className)} {...rest}>
      <div className="title-bar">
        {onPressBack && (
          <button
            className="icon-btn"
            onClick={onPressBack}
            aria-label={t("common:back", "Back")}
          >
            <ChevronLeft />
          </button>
        )}

        {Leading}

        {Icon && <div className="icon">{Icon}</div>}

        <h1 className={titleClass ?? "type-article-headline"}>
          {Title ? Title : typeof title === "string" ? title : t(...title)}
        </h1>

        {Following}

        {(showClose || onPressClose) && (
          <form method="dialog">
            <button type="submit" className="icon-btn" onClick={onPressClose}>
              <CloseIcon />
            </button>
          </form>
        )}
      </div>

      {children}
    </Header>
  );
}

type DialogBodyProps = React.HTMLAttributes<HTMLDivElement> & {
  maxHeight?: string | number;

  fade?: boolean;

  children?: React.ReactNode;
};

export function DialogBody({
  maxHeight,
  className,
  children,
  ...rest
}: DialogBodyProps) {
  return (
    <Body
      {...classNames(
        "dialog-body",
        maxHeight &&
          css({
            "--max-height":
              typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
          }),
        className,
      )}
      {...rest}
    >
      {children}
    </Body>
  );
}

export function DialogFooter({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <Footer {...classNames("dialog-footer", className)} {...rest}>
      {children}
    </Footer>
  );
}

const animPopIn = () => keyframes`
  0% {
    transform: scale(0.85);
    opacity: 0;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const animSlideIn = () => keyframes`
  0% {
    transform: translateY(100%);
  }

  100% {
    transform: translateY(0%);
  }
`;

const DialogContainer = styled("div", React.forwardRef)`
  --min-width: auto;
  --max-width: auto;

  --dialog-margin: var(--sp-2_5);
  --dialog-padding: var(--sp-6);
  --dialog-bg: var(--shade7);
  --dialog-br: var(--br-xl);
  --fade-size: var(--sp-1_5);

  container: dialog-container / inline-size;

  flex: 1;

  display: flex;
  flex-direction: column;

  min-width: calc(var(--min-width) + var(--dialog-margin));
  max-width: var(--max-width);
  margin-inline: auto;
  padding: var(--dialog-margin);

  pointer-events: none;

  transform: scale(1);
  opacity: 1;

  box-sizing: content-box;
`;

const DialogBox = styled("div", React.forwardRef)`
  max-width: min(100%, calc(100vw - var(--dialog-margin) * 2));
  max-height: 100%;
  margin-block: auto;

  background: var(--dialog-bg);
  border: 1px solid hsla(var(--shade3-hsl) / 0.15);
  border-radius: var(--dialog-br);

  overflow-y: auto;
  overscroll-behavior: contain;

  pointer-events: all;

  :root .modal-open & {
    animation: ${animPopIn} var(--transition);
  }

  ${dialogMobile} {
    & {
      position: fixed;
      left: 0;
      bottom: 0;

      width: 100vw;
      width: 100dvw;
      max-width: unset;
      max-height: calc(100vh - var(--sp-8));
      max-height: calc(100dvh - var(--sp-8));

      margin-bottom: unset;

      border-bottom: none;

      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;

      :root .modal-open & {
        animation: ${animSlideIn} var(--transition);
      }
    }
  }

  &.hide-scrollbar {
    overflow: hidden !important;
  }
`;

const Content = styled("div")`
  & > .dialog-header,
  & > .dialog-body,
  & > .dialog-footer {
    &:not(:first-child) {
      padding-top: calc(var(--dialog-padding) / 2);
    }

    &:not(:last-child) {
      padding-bottom: calc(var(--dialog-padding) / 2);
    }
  }

  & > form {
    &:not(:first-child) {
      & > .dialog-header,
      & > .dialog-body,
      & > .dialog-footer {
        padding-top: calc(var(--dialog-padding) / 2);
      }
    }

    &:not(:last-child) {
      & > .dialog-header,
      & > .dialog-body,
      & > .dialog-footer {
        padding-bottom: calc(var(--dialog-padding) / 2);
      }
    }

    &:first-child {
      & > .dialog-header,
      & > .dialog-body,
      & > .dialog-footer {
        &:not(:first-child) {
          padding-top: calc(var(--dialog-padding) / 2);
        }
      }
    }

    &:last-child {
      & > .dialog-header,
      & > .dialog-body,
      & > .dialog-footer {
        &:not(:last-child) {
          padding-bottom: calc(var(--dialog-padding) / 2);
        }
      }
    }
  }

  & > .dialog-header,
  & > .dialog-body,
  & > .dialog-footer,
  & > form > .dialog-body,
  & > form > .dialog-footer {
    &.no-gutters-top {
      padding-top: 0;
    }

    &.no-gutters-bottom {
      padding-bottom: 0;
    }

    &.no-gutters-block {
      padding-block: 0;
    }

    &.no-gutters-inline {
      padding-inline: 0;
    }

    &.no-gutters {
      padding: 0;
    }
  }
`;

const Header = styled("header")`
  --icon-btn-hit-area-scaling: 1.5;

  position: sticky;
  top: 0;

  display: flex;
  flex-direction: column;
  gap: var(--sp-6);

  width: 100%;
  padding: var(--dialog-padding);

  background: var(--dialog-bg);
  border-radius: var(--dialog-br);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;

  mask-image: linear-gradient(
    to bottom,
    black 0%,
    black calc(100% - var(--fade-size)),
    transparent 100%
  );

  z-index: 1;

  .title-bar {
    position: relative;

    display: flex;
    align-items: center;
    gap: var(--sp-3);

    .icon {
      svg {
        width: var(--sp-6);
        height: var(--sp-6);
      }
    }

    h1 {
      flex: 1;

      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    form {
      display: flex;
    }

    .icon-btn {
      transform: scale(var(--btn-hit-area-scaling));

      color: var(--shade0-50);

      background: transparent;

      cursor: pointer;

      &:hover {
        color: var(--shade0);
      }

      svg {
        width: var(--sp-6);
        height: var(--sp-6);

        transform: scale(calc(1 / var(--btn-hit-area-scaling)));
      }
    }
  }
`;

const Body = styled("div")`
  --max-height: auto;

  flex: 1;

  width: 100%;
  max-height: var(--max-height);
  padding: var(--dialog-padding);

  overflow: hidden;
`;

const Footer = styled("footer")`
  position: sticky;
  bottom: 0;

  display: flex;
  gap: var(--sp-3);

  width: 100%;
  padding: var(--dialog-padding);

  background: var(--dialog-bg);
  border-radius: var(--dialog-br);
  border-top-left-radius: 0;
  border-top-right-radius: 0;

  mask-image: linear-gradient(
    to top,
    black 0%,
    black calc(100% - var(--fade-size)),
    transparent 100%
  );

  z-index: 1;
`;
